'use client';

import {useEffect, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import Cookies from 'js-cookie';
import Preferences from '@/sdk-mobile-web/Preferences/Preferences.web';
import {createSettingsCookie} from '@/services/internal/settings';
import {areSettingsAvailable} from '@/services/internal/settings/client';
import {GAME_SETTINGS_LS_NAME, getCurrentGameCookieName, INSTALL_ID_COOKIE_NAME} from '@/utils/constant';
import type {SDKSettings} from '@/app/(app)/sdk/[gid]/types';
import LoadingScreen from '@/app/(app)/games/[gid]/_src/LoadingScreen';

/**
 * The shell game (19369) has no Unity build, so the normal settings provisioner
 * (`sdk/[gid]/ClientBoundary`) can't hydrate it — it blocks forever waiting on a Unity
 * config. Without hydrated settings, `SkillzSettingsProvider` renders null AND
 * `useSyncSettings` loops on `router.refresh()`, which is the blank-screen hang seen on
 * `/games/19369/*` (e.g. the logged-out deferred-auth create-profile route reached from
 * the root guest gate: Store/Menu → `/games/19369/play-now`).
 *
 * PartnerSdkRuntime already hydrates the shell settings this way for the partner surface;
 * this does the SAME minimal hydration (install-id + localStorage + Preferences + settings
 * cookie, no Unity/session), so the shell game's game-layout routes work for a LOGGED-OUT
 * user too. Idempotent: if settings are already available (e.g. the partner runtime ran),
 * it renders immediately. Off the shell path this component is never mounted.
 */
export default function ShellSettingsHydrator({
  settings,
  children,
}: {
  settings: SDKSettings | null;
  children: React.ReactNode;
}) {
  // No settings blob to hydrate → don't gate; fall back to the prior behavior.
  const [hydrated, setHydrated] = useState(!settings);

  useEffect(() => {
    if (hydrated || !settings) return;
    try {
      const id = String(settings.gameInfo.id);
      if (areSettingsAvailable(id)) {
        setHydrated(true);
        return;
      }
      if (!Cookies.get(INSTALL_ID_COOKIE_NAME)) {
        Cookies.set(INSTALL_ID_COOKIE_NAME, uuidv4(), {expires: 3650, path: '/'});
      }
      window.localStorage.setItem(`${GAME_SETTINGS_LS_NAME}${id}`, JSON.stringify(settings));
      if (settings.theme.settings) {
        Preferences.setAll(settings.theme.settings);
      }
      const halfMonth = 14 * 24 * 60 * 60 * 1000;
      Cookies.set(getCurrentGameCookieName(id), createSettingsCookie(id, String(Date.now())), {
        // js-cookie treats a number as DAYS; pass a Date for a real 14-day expiry.
        expires: new Date(Date.now() + halfMonth),
      });
      setHydrated(true);
    } catch (e) {
      console.error('[shell_settings] hydration failed', e);
      // Render children anyway rather than trapping the user on a loader.
      setHydrated(true);
    }
  }, [hydrated, settings]);

  if (!hydrated) return <LoadingScreen />;
  return <>{children}</>;
}
